import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Loader2, AlertTriangle, RefreshCw } from "lucide-react";

const ROLE_LABELS = {
  points_balance: "Points", tier: "Tier", dob: "DOB", doa: "DOA", store_code: "Store code",
  bill_number: "Bill no.", bill_date: "Bill date", amount: "Amount", sku_item: "SKU / item",
};
const fmtMB = (b) => (b < 1024 ** 2 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 ** 2).toFixed(1)} MB`);

function Flags({ flags }) {
  const keys = Object.keys(flags || {});
  if (!keys.length) return <span className="text-neutral-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {keys.map((k) => (
        <span key={k} className="px-1.5 py-0.5 text-[10px] bg-amber-50 border border-amber-200" title={flags[k].join(", ")}>
          {ROLE_LABELS[k] || k}: {flags[k].join(", ")}
        </span>
      ))}
    </div>
  );
}

function FileTable({ files }) {
  return (
    <table className="w-full text-xs" data-testid="inspect-files-table">
      <thead className="text-[10px] uppercase tracking-widest text-neutral-500 text-left">
        <tr><th className="py-2 pr-3">Inner file</th><th className="pr-3">Size</th><th className="pr-3">Detected type</th><th className="pr-3">Approx rows</th><th className="pr-3">Header columns</th><th>Mapping hints</th></tr>
      </thead>
      <tbody>
        {files.map((f, i) => (
          <tr key={f.name} className="border-t border-black/5 align-top" data-testid={`inspect-file-${i}`}>
            <td className="py-2 pr-3 font-medium break-all">{f.name}{f.error && <div className="text-red-600">{f.error}</div>}{f.note && <div className="text-neutral-500">{f.note}</div>}{(f.warnings || []).map((w) => <div key={w} className="text-amber-700 flex items-center gap-1" data-testid={`inspect-warning-${i}`}><AlertTriangle className="w-3 h-3 shrink-0" /> {w}</div>)}</td>
            <td className="pr-3 font-mono whitespace-nowrap">{fmtMB(f.size)}</td>
            <td className="pr-3" data-testid={`inspect-type-${i}`}><span className={f.detected_type === "unknown" ? "text-neutral-500" : "font-semibold kazo-text-burgundy"}>{f.detected_type}</span></td>
            <td className="pr-3 font-mono">{f.approx_rows == null ? "—" : f.approx_rows.toLocaleString("en-IN")}</td>
            <td className="pr-3 max-w-[320px] break-words">{(f.headers || []).join(", ") || "—"}</td>
            <td className="max-w-[320px]"><Flags flags={f.column_flags} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function InspectPanel({ file, onClose }) {
  const [res, setRes] = useState(null);
  const [run, setRun] = useState(0);
  useEffect(() => {
    let alive = true;
    let timer;
    setRes(null);
    const poll = async () => {
      const r = await api.get(`/data-files/${file.id}`);
      if (!alive) return;
      if (r.data.inspection?.status === "done") setRes(r.data.inspection);
      else timer = setTimeout(poll, 2000);
    };
    api.post(`/data-files/${file.id}/inspect${run ? "?force=true" : ""}`).then(poll).catch((e) => setRes({ ok: false, error: "Inspection failed", reason: e?.response?.data?.detail || e.message }));
    return () => { alive = false; clearTimeout(timer); };
  }, [file.id, run]);

  return (
    <div className="bg-white border border-black/10 p-6 space-y-4" data-testid="inspect-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] kazo-text-burgundy">Inspect · {file.filename}</div>
          <p className="text-xs text-neutral-500 mt-1">
            Read-only preview — nothing is imported.{res?.finished_at ? ` Saved result from ${new Date(res.finished_at).toLocaleString("en-IN")}.` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="k-btn k-btn-outline k-btn-sm" disabled={!res} onClick={() => setRun((n) => n + 1)} data-testid="inspect-rerun"><RefreshCw className="w-3.5 h-3.5" /> Re-inspect</button>
          <button className="k-btn k-btn-outline k-btn-sm" onClick={onClose} data-testid="inspect-close"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {!res && <div className="text-sm text-neutral-600 flex items-center gap-2" data-testid="inspect-running"><Loader2 className="w-4 h-4 animate-spin" /> Reading the file from storage…</div>}
      {res && !res.ok && <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3" data-testid="inspect-error"><strong>{res.error}</strong>{res.reason ? ` — ${res.reason}` : ""}</div>}
      {res?.ok && (
        <>
          <div className="text-xs text-neutral-600" data-testid="inspect-summary">{res.file_count} file(s){res.total_uncompressed ? ` · ${fmtMB(res.total_uncompressed)} uncompressed` : ""}</div>
          <FileTable files={res.files} />
        </>
      )}
    </div>
  );
}
